import { take, takeLatest, call, fork, put, all, cancel, select } from "redux-saga/effects";
import {
  checkIfIsAllowedToSeeTransaction,
  getSsdEventsFromBc,
  toUtf8,
  toHex,
  getSsdState,
  getFirstZessionarId,
  getTerminationState,
  getFourEyesState,
  getBlockNumber,
  getIdProxyAddr,
  ssdAuditEvents,
} from "services/web3Services/commons";
import { getCessionCount, getSSDCessionData } from "services/web3Services/cession";
import {
  listenForSSDEvents,
  listenForSSDPaymentEvents,
  listenForFourEyesEvents,
  listenForProviderDisconnect,
  listenForSSDRebuyEvents,
  listenForTerminationEvents,
} from "services/web3Services/eventListener";
import {
  FETCH_SSD_EVENTS_REQUEST,
  ADD_SSD_FROM_EVENT,
  FETCH_SSD_PREVIEW,
  FETCH_PAYMENT_PREVIEW,
  PROVIDER_CONNECTED,
  PDF_DOWNLOAD,
  TRADE_CONFIRMATION_DOWNLOAD,
  REBUY_CONFIRMATION_DOWNLOAD,
  TERMINATION_CONFIRMATION_DOWNLOAD,
  STOP_SSD_LISTENER,
  FETCH_SSD_EVENT_INFO_BULK_RETRY,
  EXPORT_BSSD_CSV,
  exportBssdActions,
} from "./actions";
import { ssdEventActions, updateIdProxyAddrActions, updatePreviewActions } from "./actions";
import {
  fetchSSDDetails,
  fetchSSDDetailsBulk,
  fetchRejectReason,
  fetchRebuyRejectReason,
  handleFileDownload,
  handleTradeConfirmationDownload,
  handleRebuyConfirmationDownload,
  handleTerminationConfirmationDownload,
} from "services/ssdService";
import { SSDActions, SSDStateType } from "util/constants";
import { setSsdNumberFormatBulk } from "util/convertor";
import { downloadByteArrayAsCSV, generateBssdCSV } from "util/export";
import { snackbarActions } from "redux/shared/actions";
import { appIntl } from "components/i18n/intl";
import { exportMessages } from "components/Shared/Export/messages";
import { getNameForAddr } from "services/partnerService";
import { getCompanyInfo } from "redux/selectors";

// watcher saga: watches for actions dispatched to the store, starts worker saga
export const ssdEventSagas = [
  takeLatest(FETCH_SSD_EVENTS_REQUEST, fetchSsdEvents),
  takeLatest(FETCH_SSD_EVENT_INFO_BULK_RETRY, retryFetchSsdBulkSaga),
  takeLatest(ADD_SSD_FROM_EVENT, addSsdFromEvent),
  takeLatest(FETCH_SSD_PREVIEW, fetchSsdPreviewData),
  takeLatest(FETCH_PAYMENT_PREVIEW, fetchPaymentPreviewData),
  takeLatest(PDF_DOWNLOAD, getPdf),
  takeLatest(TRADE_CONFIRMATION_DOWNLOAD, downloadTradeConfirmation),
  takeLatest(REBUY_CONFIRMATION_DOWNLOAD, downloadRebuyConfirmation),
  takeLatest(TERMINATION_CONFIRMATION_DOWNLOAD, downloadTerminationConfirmation),
  takeLatest(EXPORT_BSSD_CSV, exportCSV),
];

export function* downloadTradeConfirmation({ id, fingerprint, fingerprint_rebuy, arranger }) {
  try {
    yield call(handleTradeConfirmationDownload, id, fingerprint, fingerprint_rebuy, arranger);
  } catch (error) {
    console.error(error);
  }
}

export function* downloadRebuyConfirmation({ id, fingerprint, fingerprint_rebuy, arranger }) {
  try {
    yield call(handleRebuyConfirmationDownload, id, fingerprint, fingerprint_rebuy, arranger);
  } catch (error) {
    console.error(error);
  }
}

export function* downloadTerminationConfirmation({ ssdInfo }) {
  try {
    yield call(handleTerminationConfirmationDownload, ssdInfo);
  } catch (error) {
    console.error(error);
  }
}

export function* getPdf({
  id,
  fingerprint,
  fingerprint_rebuy,
  arranger,
  withWatermark,
  created_by_cession,
  pdfName,
  companyIdProxyAddress,
  externUrkunde,
}) {
  let downloadForZessionar = false;
  const currentSsdId = id;
  const currentSsdCessionInfo = yield call(getSSDCessionData, arranger, id);
  const currentSsd = yield call(getSsdState, arranger, id);
  const rootSsd = yield call(getSsdState, arranger, toUtf8(currentSsdCessionInfo.rootId));
  const parentSsd = yield call(getSsdState, arranger, toUtf8(currentSsdCessionInfo.parentId));
  const parentSsdCessionInfo = yield call(
    getSSDCessionData,
    arranger,
    toUtf8(currentSsdCessionInfo.parentId)
  );
  const zessionarId = yield call(
    getFirstZessionarId,
    arranger,
    toUtf8(currentSsdCessionInfo.parentId)
  );
  const zessionarSsd = yield call(getSsdState, arranger, toUtf8(zessionarId));
  const restSsd = yield call(getSsdState, arranger, toUtf8(parentSsdCessionInfo.restId));

  if (created_by_cession) {
    id = toUtf8(currentSsdCessionInfo.rootId);
    fingerprint = rootSsd.hash;
    fingerprint_rebuy = rootSsd.rebuyHash;
    withWatermark = false;
  }

  if (
    created_by_cession &&
    parentSsd.seller === currentSsd.seller &&
    companyIdProxyAddress !== parentSsd.buyer &&
    companyIdProxyAddress === currentSsd.buyer
  ) {
    downloadForZessionar = true;
  }

  yield call(
    handleFileDownload,
    id,
    fingerprint,
    fingerprint_rebuy,
    Number(toUtf8(currentSsdCessionInfo.rootId)),
    rootSsd.hash,
    rootSsd.rebuyHash,
    Number(toUtf8(currentSsdCessionInfo.parentId)),
    parentSsd.hash,
    parentSsd.rebuyHash,
    Number(toUtf8(parentSsdCessionInfo.restId)),
    restSsd.hash,
    restSsd.rebuyHash,
    Number(toUtf8(zessionarId)),
    zessionarSsd.hash,
    zessionarSsd.rebuyHash,
    arranger,
    withWatermark,
    pdfName,
    downloadForZessionar,
    currentSsdId,
    externUrkunde
  );
}

export function* startListener(companyAddr) {
  const currentBlock = yield call(getBlockNumber);
  const listener = [];
  listener.push(yield fork(providerConnectionListener, companyAddr));
  listener.push(yield fork(ssdEventListener, currentBlock));
  listener.push(yield fork(ssdPaymentEventListener, currentBlock));
  listener.push(yield fork(fourEyesEventListener, currentBlock));
  listener.push(yield fork(ssdRebuyEventListener, currentBlock));
  listener.push(yield fork(ssdTerminationEventListener, currentBlock));
  yield take(STOP_SSD_LISTENER);
  yield all(listener.map((channel) => cancel(channel)));
}

function* fetchPaymentPreviewData({ ssdStatus, modalActions, rebuyFingerprint }) {
  try {
    const ssdDetails = yield call(
      fetchSSDDetails,
      ssdStatus.ssdid,
      ssdStatus.fingerprint,
      ssdStatus.rebuyHash,
      ssdStatus.arranger
    );

    const rejectReason = yield call(
      fetchRebuyRejectReason,
      ssdStatus.ssdid,
      ssdStatus.fingerprint,
      rebuyFingerprint,
      ssdStatus.arranger
    );

    yield put(
      updatePreviewActions.updatePreviewInStore(ssdDetails.data, rejectReason.data, ssdStatus)
    );
    //show passed modal
    yield put(modalActions.open());
  } catch (error) {
    console.error(error);
  }
}

function* fetchSsdPreviewData({ ssdStatus, modalActions }) {
  try {
    const ssdDetails = yield call(
      fetchSSDDetails,
      ssdStatus.ssdid,
      ssdStatus.fingerprint,
      ssdStatus.rebuyHash,
      ssdStatus.arranger
    );

    const rejectReason = yield call(
      fetchRejectReason,
      ssdStatus.ssdid,
      ssdStatus.fingerprint,
      ssdStatus.arranger
    );

    yield put(
      updatePreviewActions.updatePreviewInStore(ssdDetails.data, rejectReason.data, ssdStatus)
    );
    //show passed modal
    yield put(modalActions.open());
  } catch (error) {
    console.error(error);
  }
}

function* addSsdFromEvent({ arranger, id }) {
  try {
    yield call(updateSsdDetails, arranger, toUtf8(id));
  } catch (error) {
    console.error(error);
  }
}

function* updateSsdDetails(arranger, id, details = null) {
  try {
    const state = yield call(getSsdState, arranger, id);
    if (checkIfIsAllowedToSeeTransaction(arranger, state.buyer, state.state)) {
      if (details === null) {
        details = yield call(fetchSSDDetails, id, state.hash, state.rebuyHash, arranger);
      }
      const auditEvents = yield call(ssdAuditEvents, toHex(id));

      let ssdRejected = false;
      let rebuyRejected = false;

      if (auditEvents.length > 0) {
        const lastAuditEvent = auditEvents.pop().returnValues;
        ssdRejected =
          lastAuditEvent.stateType === SSDStateType.SSD &&
          lastAuditEvent.action === SSDActions.REJECT_OFFER;

        rebuyRejected =
          lastAuditEvent.stateType === SSDStateType.REBUY &&
          lastAuditEvent.action === SSDActions.REJECT_OFFER;
      }

      const subid = 0;
      const fourEyesState = yield call(getFourEyesState, arranger, id, subid);

      const ssdDetails = { ...details.data };
      delete ssdDetails.fingerprint;

      const ssdCessionCount = yield call(getCessionCount, arranger, id);
      const ssdCessionData = yield call(getSSDCessionData, arranger, id);
      const terminationState = yield call(getTerminationState, arranger, id);

      const ssdData = {
        ssdDetails,
        fingerprint: state.hash,
        state: state.state,
        paymentState: state.paymentState,
        confirmationState: fourEyesState.state,
        firstConfirmer: fourEyesState.firstConfirmer,
        arranger: arranger,
        buyer: state.buyer,
        ssdid: id,
        maturityDate: state.maturityDate,
        rebuyMaturityDate: state.rebuyMaturityDate,
        rebuyState: state.rebuyState,
        rebuyHash: state.rebuyHash,
        valueDate: state.valueDate,
        ssdRejected,
        rebuyRejected,
        cessionCount: ssdCessionCount,
        ssdCessionState: ssdCessionData.cessionState,
        cessionActiveCount: ssdCessionData.activeCessionCounter,
        restId: ssdCessionData.restId,
        terminationState: terminationState.state,
        terminationType: terminationState.terminationType,
        terminationInitiator: terminationState.terminationInitiator,
        terminationHash: terminationState.hash,
        terminationDate: terminationState.terminationDate,
      };
      yield put(ssdEventActions.infoSuccess(ssdData));
    } else {
      yield put(ssdEventActions.infoNoPermission(id));
    }
  } catch (error) {
    console.error(error);
    yield put(ssdEventActions.infoFailure(id, arranger));
  }
}

export function* ssdEventListener(currentBlock) {
  try {
    const chan = yield call(listenForSSDEvents, currentBlock);
    while (true) {
      let result = yield take(chan);
      yield fork(updateSsdDetails, result.arranger, toUtf8(result.id));
      yield put(result);
    }
  } catch (error) {
    console.error(error);
  }
}

export function* ssdRebuyEventListener(currentBlock) {
  try {
    const chan = yield call(listenForSSDRebuyEvents, currentBlock);
    while (true) {
      let result = yield take(chan);
      yield fork(updateSsdDetails, result.arranger, toUtf8(result.id));
      yield put(result);
    }
  } catch (error) {
    console.error(error);
  }
}

export function* ssdPaymentEventListener(currentBlock) {
  try {
    const chan = yield call(listenForSSDPaymentEvents, currentBlock);
    while (true) {
      let result = yield take(chan);
      yield put(result);
    }
  } catch (error) {
    console.error(error);
  }
}

export function* ssdTerminationEventListener(currentBlock) {
  try {
    const chan = yield call(listenForTerminationEvents, currentBlock);
    while (true) {
      let result = yield take(chan);
      yield put(result);
    }
  } catch (error) {
    console.error(error);
  }
}

export function* fourEyesEventListener(currentBlock) {
  try {
    const chan = yield call(listenForFourEyesEvents, currentBlock);
    while (true) {
      let result = yield take(chan);
      yield fork(updateSsdDetails, result.arranger, toUtf8(result.id));
      yield put(result);
    }
  } catch (error) {
    console.error(error);
  }
}

export function* providerConnectionListener(companyAddr) {
  try {
    const chan = yield call(listenForProviderDisconnect);
    while (true) {
      let result = yield take(chan);
      if (result.type === PROVIDER_CONNECTED) {
        yield call(fetchSsdEvents, { companyAddr });
      }
    }
  } catch (error) {
    console.error(error);
  }
}

export function* retryFetchSsdBulkSaga({ loadErrors, arranger }) {
  const processedErrors = yield call(processSsdErrorsForRetry, loadErrors, arranger);
  yield call(ssdBulkRequest, processedErrors);
}

export function* fetchBundleDetails(bundle) {
  const arranger = bundle[0].arranger;
  const response = yield call(fetchSSDDetailsBulk, arranger, bundle);
  if (response.status >= 200 && response.status < 300) {
    const ssdInfos = response.data.ssdResponses.map((detail) => setSsdNumberFormatBulk(detail));
    yield all(
      ssdInfos.map((ssdInfo) =>
        fork(updateSsdDetails, ssdInfo.data.darlehensnehmer, `${ssdInfo.data.ssdid}`, ssdInfo)
      )
    );

    const requestedIds = bundle.map((element) => element.ssdId);
    const fetchedIds = ssdInfos.map((ssdInfo) => `${ssdInfo.data.ssdid}`);

    const missingIds = requestedIds.filter((id) => !fetchedIds.includes(id));
    if (missingIds.length > 0) {
      yield put(ssdEventActions.infoBulkFailure(missingIds, arranger));
    }
  } else {
    const ids = bundle.map((item) => item.ssdId);
    yield put(ssdEventActions.infoBulkFailure(ids, arranger));
  }
}

export function* fetchSsdBulkInfo(ssdRequestBundle) {
  const numTotal = ssdRequestBundle.map((bundle) => bundle.length).reduce((a, b) => a + b, 0);
  yield put(ssdEventActions.setTotal(numTotal));
  yield all(ssdRequestBundle.map((bundle) => fork(fetchBundleDetails, bundle)));
}

const processSsdEventsAscii = (ssdEventsAscii) =>
  ssdEventsAscii.map((event) => ({
    id: event.returnValues.id,
    arranger: event.returnValues.arranger,
  }));

const processSsdErrorsForRetry = (ssdErrorsForRetry, arrangerFilter) => {
  const processedErrors = [];

  Object.keys(ssdErrorsForRetry).forEach((arranger) => {
    ssdErrorsForRetry[arranger].forEach((ssdId) => {
      processedErrors.push({ id: ssdId, arranger });
    });
  });

  return arrangerFilter
    ? processedErrors.filter((item) => item.arranger === arrangerFilter)
    : processedErrors;
};

export function* ssdBulkRequest(ssdIdsWithArranger) {
  let ssdRequestBundle = [];
  for (let item of ssdIdsWithArranger) {
    const state = yield call(getSsdState, item.arranger, item.id);
    if (ssdRequestBundle.length === 0) {
      ssdRequestBundle.push([
        {
          ssdId: item.id,
          fingerprint: state.hash,
          fingerprint_rebuy: state.rebuyHash,
          arranger: item.arranger,
        },
      ]);
    } else {
      let hit = false;
      for (let bundle of ssdRequestBundle) {
        if (item.arranger === bundle[0].arranger) {
          hit = true;
          bundle.push({
            ssdId: item.id,
            fingerprint: state.hash,
            fingerprint_rebuy: state.rebuyHash,
            arranger: item.arranger,
          });
        }
      }
      if (!hit) {
        ssdRequestBundle.push([
          {
            ssdId: item.id,
            fingerprint: state.hash,
            fingerprint_rebuy: state.rebuyHash,
            arranger: item.arranger,
          },
        ]);
      }
    }
  }
  yield call(fetchSsdBulkInfo, ssdRequestBundle);
}

export function* fetchSsdEvents({ companyAddr }) {
  try {
    const ssdEvents = yield call(getSsdEventsFromBc, companyAddr);
    const ssdEventsAscii = [...ssdEvents].map((event) => {
      return {
        ...event,
        returnValues: {
          ...event.returnValues,
          id: toUtf8(event.returnValues.id),
        },
      };
    });

    const processedEvents = yield call(processSsdEventsAscii, ssdEventsAscii);
    yield call(ssdBulkRequest, processedEvents);

    //update idProxyAddr in Store so components can check if SSD arranger == idProxyAddr for logged in wallet
    const idProxyAddr = yield call(getIdProxyAddr);
    yield put(updateIdProxyAddrActions.update(idProxyAddr));

    //start eventlistener
    yield call(startListener, companyAddr);
  } catch (error) {
    console.error(error);
  }
}

export function* exportCSV({ bssds }) {
  try {
    const companyInfo = yield select(getCompanyInfo);
    const bssdsCompatibility = bssds.map((bssd) => ({
      ...bssd,
      darlehensnehmer_name:
        bssd.darlehensnehmer_name || getNameForAddr(bssd.darlehensnehmer, companyInfo),
      darlehensgeber_name:
        bssd.darlehensgeber_name || getNameForAddr(bssd.darlehensgeber, companyInfo),
    }));
    const bytes = yield generateBssdCSV(bssdsCompatibility);
    yield put(exportBssdActions.success());
    downloadByteArrayAsCSV("Export.csv", bytes);
  } catch (error) {
    yield put(exportBssdActions.failure());
    yield put(snackbarActions.openError(appIntl().formatMessage(exportMessages.export_failure)));
  }
}
